import { TimelinePost, Meeting } from '../models/Content';
import { logPostActivity } from './adminActivityDatabase.ts';
import { getTrackingInfo } from '../utils/trackingUtils.ts';
import { getUserByUsername, getAllUsers } from './userDatabase.ts';
import { getDisplayName } from '../utils/userUtils';
import { db } from '../config/firebase.ts';
import { collection, doc, getDocs, setDoc, updateDoc, deleteDoc, query, orderBy, Timestamp, where } from 'firebase/firestore';

// Initialize Firestore collections
export const initializeContent = async (): Promise<void> => {
  try {
    // Load posts
    const postsRef = collection(db, 'posts');
    const postsSnapshot = await getDocs(postsRef);
    if (postsSnapshot.empty) {
  
    }
  } catch (error) {
    console.error('Failed to initialize content:', error);
    throw error;
  }
};

// Generic function to get all posts of a specific category
export const getAllPosts = async (category: string): Promise<any[]> => {
  if (!category) return [];
  try {
    const postsRef = collection(db, 'posts');
    const q = query(postsRef, 
      where('category', '==', category),
      orderBy('created_at', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    // Map the documents and ensure all required fields are present
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        created_at: data.created_at || Timestamp.now(),
        category: category,
        authorId: data.authorId || 'unknown'
      };
    });
  } catch (error) {
    console.error(`Failed to get ${category} posts:`, error);
    if (error instanceof Error && error.message.includes('permission-denied')) {
      throw new Error('You do not have permission to access these posts. Please check your login status.');
    }
    throw error;
  }
};

// Generic function to add a post
export const addPost = async (post: any): Promise<any> => {
  if (!post.category) throw new Error('Post category is required');
  if (!post.authorId) throw new Error('Author ID is required');
  
  try {
    const postId = post.id || `${post.category}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const postData = {
      ...post,
      id: postId,
      created_at: post.created_at || Timestamp.now(),
      authorId: post.authorId
    };

    await setDoc(doc(db, 'posts', postId), postData);

    // Track activity
    const allUsers = await getAllUsers();
    const user = allUsers.find((u: any) => u.id === post.authorId);
    const trackingInfo = getTrackingInfo(user?.id);
    logPostActivity(
      user?.id || 'unknown',
      user ? getDisplayName(user) : 'unknown',
      'create',
      post.category,
      postId,
      post.title || 'Untitled',
      {},
      trackingInfo.ipAddress,
      trackingInfo.userAgent
    );

    // Return the saved post with the generated ID
    return postData;
  } catch (error) {
    console.error(`Failed to add ${post.category} post:`, error);
    if (error instanceof Error && error.message.includes('permission-denied')) {
      throw new Error('You do not have permission to create posts. Please check your login status.');
    }
    throw error;
  }
};

// Generic function to update a post
export const updatePost = async (post: any): Promise<void> => {
  try {
    if (!post.id) throw new Error('Post ID is required for update');
    await updateDoc(doc(db, 'posts', post.id), post);
    // Track activity
    const allUsers = await getAllUsers();
    const user = allUsers.find((u: any) => u.id === post.authorId);
    const trackingInfo = getTrackingInfo(user?.id);
    logPostActivity(
      user?.id || 'unknown',
      user ? getDisplayName(user) : 'unknown',
      'edit',
      post.category,
      post.id,
      post.title || 'Untitled',
      {},
      trackingInfo.ipAddress,
      trackingInfo.userAgent
    );
  } catch (error) {
    console.error(`Failed to update ${post.category} post:`, error);
    throw error;
  }
};

// Generic function to delete a post
export const deletePost = async (post: any): Promise<void> => {
  try {
    if (!post.id) throw new Error('Post ID is required for deletion');
    await deleteDoc(doc(db, 'posts', post.id));
    // Track activity
    const allUsers = await getAllUsers();
    const user = allUsers.find((u: any) => u.id === post.authorId);
    const trackingInfo = getTrackingInfo(user?.id);
    logPostActivity(
      user?.id || 'unknown',
      user ? getDisplayName(user) : 'unknown',
      'delete',
      post.category,
      post.id,
      post.title || 'Untitled',
      {},
      trackingInfo.ipAddress,
      trackingInfo.userAgent
    );
  } catch (error) {
    console.error(`Failed to delete ${post.category} post:`, error);
    throw error;
  }
};

 